import { Button, Group, TextInput } from '@mantine/core'
import { isEmail, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconUserPlus } from '@tabler/icons-react'
import { useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { UserApiDTO } from '../../codegen/generated/Api.schemas'
import { usePostApiUsers } from '../../codegen/generated/users'

type CreateUserProps = {
  onCreate: (userId: UserApiDTO['id']) => void
}

export const CreateUser = ({ onCreate }: CreateUserProps) => {
  const t = useTranslate()
  const form = useForm({
    initialValues: { email: '' },
    validate: {
      email: isEmail(t('errors.invalidEmail')),
    },
  })

  const createUserMutation = usePostApiUsers()

  const onCreateUser = useCallback(async () => {
    try {
      const { email } = form.values
      const res = await createUserMutation.mutateAsync({
        data: {
          email,
        },
      })

      onCreate(res.body.id)
    } catch (error: any) {
      console.error(error)
      let message = t('_generics.unknownError')
      if (error.response?.data?.content?.missingPermission) {
        message = `${t('pages.users.lackingPermission')} → ${t(
          `pages.users.permissionsList.${error.response.data.content.missingPermission}`,
        )}`
      } else if (error.response?.message) {
        message = error.response.message
      }

      notifications.show({
        color: 'red',
        title: `${t('_generics.error')} - ${error.response?.status}`,
        message,
      })
    }
  }, [form.values, createUserMutation.mutateAsync, onCreate, t])

  return (
    <form onSubmit={form.onSubmit(onCreateUser)}>
      <TextInput
        key={form.key('email')}
        mt="md"
        label={t('_generics.email')}
        placeholder={t('placeholders.email')}
        {...form.getInputProps('email')}
      />
      <Group justify="flex-end" mt="xl">
        <Button
          type="submit"
          size="compact-md"
          leftSection={<IconUserPlus size={14} />}
          disabled={!form.isValid()}
          loading={createUserMutation.isLoading}
        >
          {t('_generics.create')}
        </Button>
      </Group>
    </form>
  )
}
